/**
 * Image Preview Dialog Component
 *
 * Displays image preview in a modal dialog
 * Supports navigation between images in the same folder
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { FolderNode } from '../../models/FileStorage';
import { isImageFile } from './FolderListView';
import { apiClient } from '../../services/api/apiClient';
import { API_ENDPOINTS } from '../../config/api';

interface ImagePreviewDialogProps {
  open: boolean;
  imageFile: FolderNode | null;
  projectId: string;
  category: string;
  allFiles: FolderNode[];
  onClose: () => void;
  onDownload: (filename: string) => void;
}

/**
 * ImagePreviewDialog Component
 *
 * Features:
 * - Full-size image preview
 * - Previous/Next navigation
 * - Download button
 * - Loading state
 * - Error handling
 */
export const ImagePreviewDialog: React.FC<ImagePreviewDialogProps> = ({
  open,
  imageFile,
  projectId,
  category,
  allFiles,
  onClose,
  onDownload,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Store projectId when dialog opens to prevent it from changing during viewing
  const projectIdRef = useRef<string>(projectId);

  // Update projectIdRef only when dialog opens
  useEffect(() => {
    if (open) {
      projectIdRef.current = projectId;
    }
  }, [open, projectId]);

  // Filter only image files (memoize to prevent re-render loops)
  const imageFiles = useMemo(
    () => allFiles.filter((file) => !file.isFolder && isImageFile(file.name)),
    [allFiles]
  );

  // Find current image index only when the selected image changes (not on navigation)
  useEffect(() => {
    if (imageFile) {
      const index = imageFiles.findIndex((f) => f.path === imageFile.path);
      setCurrentIndex(index !== -1 ? index : 0);
    }
  }, [imageFile?.path, imageFiles]);

  const currentFile = imageFiles[currentIndex] || imageFile;

  // Fetch image via authenticated API client
  const fetchImage = useCallback(async (filePath: string) => {
    setLoading(true);
    setError(false);
    // Revoke previous blob URL via ref (avoids stale closure)
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
      setBlobUrl(null);
    }
    try {
      const encodedPath = filePath
        .split('/')
        .map(segment => encodeURIComponent(segment))
        .join('/');
      const endpoint = API_ENDPOINTS.FILE_STORAGE.DOWNLOAD(
        projectIdRef.current,
        category,
        encodedPath
      );
      const response = await apiClient.get(endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      blobUrlRef.current = url;
      setBlobUrl(url);
      setLoading(false);
    } catch {
      setError(true);
      setLoading(false);
    }
  }, [category]);

  // Fetch image when dialog opens or current file changes
  useEffect(() => {
    if (open && currentFile) {
      fetchImage(currentFile.path);
    }
    return () => {
      // Cleanup blob URL when dialog closes or file changes
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [open, currentFile?.path]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!imageFile) {
    return null;
  }

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < imageFiles.length - 1;

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleDownload = () => {
    onDownload(currentFile.path);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" noWrap sx={{ flex: 1, mr: 2 }}>
            {currentFile.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={handleDownload} title="다운로드">
              <DownloadIcon />
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        {imageFiles.length > 1 && (
          <Typography variant="caption" color="text.secondary">
            {currentIndex + 1} / {imageFiles.length}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 2,
        }}
      >
        {/* Previous Button */}
        {imageFiles.length > 1 && hasPrevious && (
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'background.paper',
                boxShadow: 4,
              },
            }}
          >
            <PrevIcon />
          </IconButton>
        )}

        {/* Image */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            position: 'relative',
          }}
        >
          {loading && (
            <CircularProgress
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            />
          )}

          {error ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="error" gutterBottom>
                이미지를 불러올 수 없습니다.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                파일이 손상되었거나 접근할 수 없습니다.
              </Typography>
            </Box>
          ) : blobUrl ? (
            <img
              src={blobUrl}
              alt={currentFile.name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          ) : null}
        </Box>

        {/* Next Button */}
        {imageFiles.length > 1 && hasNext && (
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              bgcolor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                bgcolor: 'background.paper',
                boxShadow: 4,
              },
            }}
          >
            <NextIcon />
          </IconButton>
        )}
      </DialogContent>
    </Dialog>
  );
};


